import { PropsWithChildren, ReactNode, useState } from 'react';

import {
  ExternalCell,
  ExternalRow,
  ShadowDivider,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';

import { PreviewJson } from 'components/code';

import { EventViewToggle } from './EventViewToggle';

import { PreparedEvent } from '../../../types';
import type { StackProps } from '@cast/design-system';

type DetailWithTitleProps = {
  title: string;
  detail?: ReactNode;
  testId?: string;
  titleVariant?: 'strong' | 'medium' | 'light';
};
export const DetailWitTitle = ({
  title,
  detail,
  testId,
  titleVariant = 'medium',
}: DetailWithTitleProps) => {
  return (
    <Stack direction="row" alignItems="center" gap={8}>
      <Typography
        variant={titleVariant === 'strong' ? 'L10M' : 'L10R'}
        color={
          titleVariant === 'light'
            ? 'grey.500'
            : titleVariant === 'medium'
            ? 'grey.600'
            : undefined
        }
      >
        {title}
      </Typography>
      <Typography variant="P10R" data-testid={testId}>
        {detail}
      </Typography>
    </Stack>
  );
};

type Props = PropsWithChildren<{
  event: PreparedEvent;
  titleRowContent?: ReactNode;
  titleRowProps?: StackProps;
  jsonOnly?: boolean;
}>;

export const DetailsRowContainer = ({
  event,
  titleRowContent,
  titleRowProps,
  children,
  jsonOnly = false,
}: Props) => {
  const [isJson, setIsJson] = useState(jsonOnly);

  return (
    <Stack
      data-testid="details-row-container"
      sx={{
        '& .Ds-Table-ExternalRow + .Ds-Table-ExternalRow': {
          borderTop: tokens.theme.border.structural.neutral,
        },
        '& .Ds-Table-ExternalRow .DS-Table-CellWrapper:first-of-type': {
          marginLeft: 40,
        },
        '& .Ds-Table-ExternalRow:last-of-type:not(:first-of-type)': {
          marginBottom: 8,
        },
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Box sx={{ position: 'relative' }}>
        {!isJson && (
          <ExternalRow height={48} {...titleRowProps}>
            <ExternalCell
              startColumn={0}
              cellProps={{
                pr: 16,
              }}
            >
              <DetailWitTitle
                title="Event:"
                detail={event.displayName}
                testId="event"
                titleVariant="light"
              />
            </ExternalCell>
            {titleRowContent}
          </ExternalRow>
        )}
        <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
          <EventViewToggle
            value={isJson}
            disabled={jsonOnly}
            onChange={() => setIsJson((isJson) => !isJson)}
          />
        </Box>
      </Box>
      {isJson ? (
        <PreviewJson
          data={event}
          actionsProps={{ sx: { right: 75, top: 12 } }}
        />
      ) : (
        children
      )}
      <ShadowDivider
        size={16}
        opacity={0.3}
        direction="top"
        sx={!children ? { position: 'absolute', bottom: 0 } : undefined}
      />
    </Stack>
  );
};
